import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import { assignMetadata } from '../../utils/assign-metadata';
import {
  addAgentVoice,
  addFavoriteVoice,
  createAgentVoiceSettings,
  createDisclaimerVoiceSettings,
  deleteAgentVoice,
  removeFavoriteVoice,
  updateAgentVoice,
  updateAgentVoiceSettings,
  updateDisclaimerVoice,
  updateDisclaimerVoiceSettings,
} from './actions';
import type {
  AgentVoice,
  DisclaimerVoice,
  FavoriteVoice,
  VoiceManagement,
  VoiceSettings,
} from './types';

const agentVoiceAdapter = createEntityAdapter<AgentVoice>();
const agentVoiceSelectors = agentVoiceAdapter.getSelectors();

const agentVoiceSettingsAdapter = createEntityAdapter<VoiceSettings>();
const agentVoiceSettingsSelectors = agentVoiceSettingsAdapter.getSelectors();

const disclaimerVoiceSettingsAdapter = createEntityAdapter<VoiceSettings>();
const disclaimerVoiceSettingsSelectors =
  disclaimerVoiceSettingsAdapter.getSelectors();
const disclaimerVoicesAdapter = createEntityAdapter<DisclaimerVoice>();
const disclaimerVoicesSelectors = disclaimerVoicesAdapter.getSelectors();

const favoriteVoicesAdapter = createEntityAdapter<FavoriteVoice>();
const favoriteVoicesSelectors = favoriteVoicesAdapter.getSelectors();

const initialState = {
  agentVoices: agentVoiceAdapter.getInitialState(),
  agentVoiceSettings: agentVoiceSettingsAdapter.getInitialState(),
  disclaimerVoiceSettings: disclaimerVoiceSettingsAdapter.getInitialState(),
  disclaimerVoices: disclaimerVoicesAdapter.getInitialState(),
  favoriteVoices: favoriteVoicesAdapter.getInitialState(),
} satisfies VoiceManagement;

const slice = createSlice({
  name: 'voices',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(addAgentVoice, (state, action) => {
      const agentVoice: AgentVoice = {
        id: action.payload.id,
        voiceId: action.payload.voiceId,
        probability: action.payload.probability,
        updatedAt: '',
        updatedBy: '',
        languageCode: action.payload.languageCode,
      };
      assignMetadata(action, agentVoice);
      agentVoiceAdapter.addOne(state.agentVoices, agentVoice);
    });

    builder.addCase(updateAgentVoice, (state, action) => {
      const entityId = action.payload.id;
      if (!entityId) {
        return;
      }

      const existingVoice = state.agentVoices.entities[entityId];

      const voiceId = action.payload.newVoiceId || existingVoice?.voiceId;

      const agentVoice: AgentVoice = {
        id: entityId,
        voiceId,
        probability: action.payload.probability,
        updatedAt: '',
        updatedBy: '',
        languageCode: existingVoice?.languageCode,
      };
      assignMetadata(action, agentVoice);
      agentVoiceAdapter.updateOne(state.agentVoices, {
        id: entityId,
        changes: agentVoice,
      });
    });

    builder.addCase(deleteAgentVoice, (state, action) => {
      if (!action.payload.id) {
        return;
      }
      agentVoiceAdapter.removeOne(state.agentVoices, action.payload.id);
    });

    builder.addCase(createAgentVoiceSettings, (state, action) => {
      const agentVoiceSettings: VoiceSettings = {
        id: action.payload.voiceId,
        provider: action.payload.settings.$case,
        settings: action.payload.settings.value,
        createdBy: '',
        createdAt: '',
        updatedBy: '',
        updatedAt: '',
      };
      assignMetadata(action, agentVoiceSettings);
      agentVoiceSettingsAdapter.addOne(
        state.agentVoiceSettings,
        agentVoiceSettings,
      );
    });

    builder.addCase(createDisclaimerVoiceSettings, (state, action) => {
      const disclaimerVoiceSettings: VoiceSettings = {
        id: action.payload.voiceId,
        provider: action.payload.settings.$case,
        settings: action.payload.settings.value,
        createdBy: '',
        createdAt: '',
        updatedBy: '',
        updatedAt: '',
      };
      assignMetadata(action, disclaimerVoiceSettings);
      disclaimerVoiceSettingsAdapter.addOne(
        state.disclaimerVoiceSettings,
        disclaimerVoiceSettings,
      );
    });

    builder.addCase(updateDisclaimerVoice, (state, action) => {
      const id = action.payload.languageCode ?? 'default';
      const disclaimerVoice: DisclaimerVoice = {
        id,
        voiceId: action.payload.voiceId,
        updatedBy: '',
        updatedAt: '',
        languageCode: action.payload.languageCode,
      };
      assignMetadata(action, disclaimerVoice);
      disclaimerVoicesAdapter.upsertOne(
        state.disclaimerVoices,
        disclaimerVoice,
      );
    });

    builder.addCase(updateAgentVoiceSettings, (state, action) => {
      const agentVoiceSettings: VoiceSettings = {
        id: action.payload.voiceId,
        provider: action.payload.settings.$case,
        settings: action.payload.settings.value,
        createdBy: '',
        createdAt: '',
        updatedBy: '',
        updatedAt: '',
      };
      assignMetadata(action, agentVoiceSettings);
      agentVoiceSettingsAdapter.updateOne(state.agentVoiceSettings, {
        id: action.payload.voiceId,
        changes: agentVoiceSettings,
      });
    });

    builder.addCase(updateDisclaimerVoiceSettings, (state, action) => {
      const disclaimerVoiceSettings: VoiceSettings = {
        id: action.payload.voiceId,
        provider: action.payload.settings.$case,
        settings: action.payload.settings.value,
        createdBy: '',
        createdAt: '',
        updatedBy: '',
        updatedAt: '',
      };
      assignMetadata(action, disclaimerVoiceSettings);
      disclaimerVoiceSettingsAdapter.updateOne(state.disclaimerVoiceSettings, {
        id: action.payload.voiceId,
        changes: disclaimerVoiceSettings,
      });
    });

    builder.addCase(addFavoriteVoice, (state, action) => {
      const favoriteVoice: FavoriteVoice = {
        id: action.payload.voiceId,
        voiceId: action.payload.voiceId,
        accountId: action.payload.accountId,
        createdBy: '',
        createdAt: '',
        updatedBy: '',
        updatedAt: '',
      };
      assignMetadata(action, favoriteVoice);
      favoriteVoicesAdapter.upsertOne(state.favoriteVoices, favoriteVoice);
    });

    builder.addCase(removeFavoriteVoice, (state, action) => {
      favoriteVoicesAdapter.removeOne(
        state.favoriteVoices,
        action.payload.voiceId,
      );
    });
  },
});

export {
  agentVoiceAdapter,
  agentVoiceSettingsAdapter,
  disclaimerVoicesAdapter,
  disclaimerVoiceSettingsAdapter,
  favoriteVoicesAdapter,
};

export {
  agentVoiceSelectors,
  agentVoiceSettingsSelectors,
  disclaimerVoiceSettingsSelectors,
  disclaimerVoicesSelectors,
  favoriteVoicesSelectors,
};

export {
  addAgentVoice,
  addFavoriteVoice,
  createAgentVoiceSettings,
  createDisclaimerVoiceSettings,
  deleteAgentVoice,
  removeFavoriteVoice,
  updateAgentVoice,
  updateAgentVoiceSettings,
  updateDisclaimerVoice,
  updateDisclaimerVoiceSettings,
};

export type { AgentVoice, FavoriteVoice, VoiceManagement, VoiceSettings };

export default slice.reducer;
